import type { ReactNode } from "react";
import { Link } from "react-router";

import { splitProseSegments } from "../lib/legal/formatProse";
import { parseLegalContent } from "../lib/legal/parseTable";
import { useLocale } from "../hooks/useLocale";
import { LegalTable } from "./LegalTable";

type LegalBodyProps = {
  text: string;
};

function linkify(text: string, pathFor: (p: string) => string): ReactNode[] {
  const parts: ReactNode[] = [];
  const re =
    /(info@albavox\.com|privacy@albavox\.com|dpo@albavox\.com|albavox\.com\/[a-z0-9/-]+|albavox\.com)/gi;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      parts.push(text.slice(last, match.index));
    }
    const value = match[0];
    if (value.includes("@")) {
      parts.push(
        <a
          key={match.index}
          href={`mailto:${value}`}
          className="text-brand-500 underline-offset-2 hover:underline"
        >
          {value}
        </a>,
      );
    } else if (value.startsWith("albavox.com/")) {
      const path = `/${value.replace("albavox.com", "")}`;
      parts.push(
        <Link
          key={match.index}
          to={pathFor(path)}
          className="text-brand-500 underline-offset-2 hover:underline"
        >
          {value}
        </Link>,
      );
    } else {
      parts.push(
        <a
          key={match.index}
          href="https://www.albavox.com"
          className="text-brand-500 underline-offset-2 hover:underline"
        >
          {value}
        </a>,
      );
    }
    last = match.index + value.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : [text];
}

function ProseBlock({
  text,
  pathFor,
}: {
  text: string;
  pathFor: (p: string) => string;
}) {
  const segments = splitProseSegments(text);

  return (
    <>
      {segments.map((segment, i) => {
        if (segment.type === "subheading") {
          return (
            <h3
              key={i}
              className="pt-2 text-lg font-semibold text-text-heading sm:text-xl"
            >
              {segment.text}
            </h3>
          );
        }

        if (segment.type === "list") {
          return (
            <div key={i} className="space-y-3">
              {segment.intro ? (
                <p className="text-base leading-relaxed text-text-body">
                  {linkify(segment.intro, pathFor)}
                </p>
              ) : null}
              <ul className="list-disc space-y-2 ps-5 text-base leading-relaxed text-text-body">
                {segment.items.map((item, j) => (
                  <li key={j}>{linkify(item, pathFor)}</li>
                ))}
              </ul>
            </div>
          );
        }

        return (
          <p key={i} className="text-base leading-relaxed text-text-body">
            {linkify(segment.text, pathFor)}
          </p>
        );
      })}
    </>
  );
}

export function LegalBody({ text }: LegalBodyProps) {
  const { pathFor } = useLocale();
  const blocks = parseLegalContent(text);
  const renderCell = (cell: string) => linkify(cell, pathFor);

  return (
    <div className="space-y-5">
      {blocks.map((block, i) => {
        if (block.type === "table") {
          return (
            <LegalTable
              key={i}
              table={block.table}
              renderCell={renderCell}
            />
          );
        }
        return (
          <div key={i} className="space-y-4">
            <ProseBlock text={block.text} pathFor={pathFor} />
          </div>
        );
      })}
    </div>
  );
}
